import { memo, useCallback, useState } from 'react';
import {
  BottomSheet,
  useNavigation,
  StackNavigationProp,
  CommonActions,
} from '@betty/navigation';
import { useTranslation } from '@betty/i18n';
import { logError } from '@betty/toolkit/logging';
import {
  Box,
  Text,
  Icon,
  Asset,
  Button,
  ScrollView,
  UploadImages,
  formatFormDataImage,
} from '@betty/moonlight';
import { PaymentMethodVerificationNavigatorParamsList } from '../../../navigators/PaymentMethodVerificationNavigator/PaymentMethodVerificationNavigator.types';
import { PaymentMethodVerificationImagesProps } from './PaymentMethodVerificationImages.types';
import DocsService from '../../../services/DocsService/DocsService';

function PaymentMethodVerificationImages({
  paymentEntityId,
  hasMultiplePayments,
}: PaymentMethodVerificationImagesProps) {
  const { t } = useTranslation('common');
  const navigation =
    useNavigation<
      StackNavigationProp<PaymentMethodVerificationNavigatorParamsList>
    >();
  const [frontImages, setFrontImages] = useState<Asset[]>([]);
  const [backImages, setBackImages] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true);

      const data = new FormData();

      data.append('paymentEntityId', paymentEntityId as never);

      [...frontImages, ...backImages].forEach((image) => {
        data.append('Files', formatFormDataImage(image));
      });

      await DocsService.uploadPaymentMethodDocs({ data });

      navigation.dispatch(
        hasMultiplePayments
          ? CommonActions.reset({
              index: 1,
              routes: [
                { name: 'PaymentMethodVerificationCardsScreen' },
                {
                  name: 'PaymentMethodVerificationSuccessScreen',
                  params: { hasMultiplePayments },
                },
              ],
            })
          : CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'PaymentMethodVerificationSuccessScreen',
                  params: { hasMultiplePayments },
                },
              ],
            }),
      );
    } catch (error) {
      logError('Payment method verification request failure.', error);

      navigation.navigate('PaymentMethodVerificationFailureScreen', {
        hasMultiplePayments,
      });
    } finally {
      setLoading(false);
    }
  }, [
    paymentEntityId,
    hasMultiplePayments,
    frontImages,
    backImages,
    navigation,
  ]);

  const onSizeLimitReached = useCallback(
    (sizeLimit: number) => {
      BottomSheet.open({
        namespace: 'common',
        type: 'InfoImage',
        data: {
          titleNS: 'common',
          titleKey: 'errorTitle',
          description: t('imagesMaxSizeReached', {
            sizeLimit,
            sizeUnit: 'MB',
            count: 1,
          }),
        },
      });
    },
    [t],
  );

  return (
    <ScrollView
      contentContainerStyle={{
        gap: 'space-400',
        minHeight: '100%',
        paddingTop: 'space-400',
        paddingBottom: 'safe-area-inset-bottom',
        paddingHorizontal: 'space-600',
      }}
    >
      <Text
        variant="h5"
        locKey="uploadPhotos"
        locNS="wallet"
        color="darkSurfacePrimary"
      />
      <Box flexDirection="row" gap="space-400">
        <UploadImages
          flex={1}
          mediaType="photo"
          titleNS="wallet"
          titleKey="front"
          images={frontImages}
          sizeLimit={10}
          selectionLimit={1 - frontImages.length}
          onChange={setFrontImages}
          onSizeLimitReached={onSizeLimitReached}
        />
        <UploadImages
          flex={1}
          mediaType="photo"
          titleNS="wallet"
          titleKey="back"
          images={backImages}
          sizeLimit={10}
          selectionLimit={1 - backImages.length}
          onChange={setBackImages}
          onSizeLimitReached={onSizeLimitReached}
        />
      </Box>
      <Box
        gap="space-200"
        padding="space-200"
        borderRadius="radius-200"
        backgroundColor="mutedBackground"
      >
        <Box flexDirection="row" alignItems="center" gap="space-200">
          <Icon
            name="info"
            width="space-600"
            height="space-600"
            color="iconSecondary"
          />
          <Text
            variant="caption-semi"
            locNS="wallet"
            locKey="paymentMethodVerificationCautionTitle"
            color="darkSurfacePrimary"
          />
        </Box>
        <Text
          variant="caption"
          locNS="wallet"
          locKey="paymentMethodVerificationCautionDescription"
          color="darkSurfacePrimary"
          paddingLeft="space-800"
        />
      </Box>
      <Box flex={1} justifyContent="flex-end" gap="space-400">
        <Button
          locNS="wallet"
          locKey="paymentMethodVerificationCautionButton"
          disabled={!frontImages.length || !backImages.length}
          size="large"
          loading={loading}
          onPress={onSubmit}
        />
        <Button
          variant="secondary"
          locNS="common"
          locKey="goBack"
          size="large"
          onPress={navigation.goBack}
        />
      </Box>
    </ScrollView>
  );
}

export default memo(PaymentMethodVerificationImages);
